import { useEffect, useRef, useState } from "react";
import { PreferencesModalInterface } from "../../assets/interfaces/interfaces";
import {
  Modal,
  IconButton,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../redux/store";
import { updateUserPreference } from "../../redux/slices/userPreferencesSlice";
import { CrossIcon } from "../Plans/icons/GPTIcons/CrossIcon";
import PreferenceItem from "../ChatMessage/PreferenceItem";
import { showNotification } from "../../redux/slices/notificationSlice";
import WarningModal from "../Shared/WarningModal";

type TradingFocusOptions = "equity" | "options" | "any";
type RiskAppetiteOptions = "high" | "medium" | "small" | "any";

export default function PreferencesModal({
  open,
  setOpen,
}: PreferencesModalInterface) {
  const dispatch = useDispatch<AppDispatch>();
  const userPreferences = useSelector(
    (state: { userPreferences }) => state.userPreferences,
  );
  const [tradingFocus, setTradingFocus] = useState<TradingFocusOptions>("any");
  const [riskAppetite, setRiskAppetite] = useState<RiskAppetiteOptions>("any");
  const [isPreferencesSubmitted, setIsPreferencesSubmitted]
    = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  const initialLoadRef = useRef(true); // tracking to make sure that initial prefs is only sent once
  const updatePreferences = async () => {
    const updatedPreferences = {
      tradingFocus,
      riskAppetite,
    };
    await dispatch(
      updateUserPreference({
        userId: userPreferences.userId,
        preferences: updatedPreferences,
      }),
    );
  };

  // if component is opened for the first time (userPreferences.hasOpenedModal is false), send an initial POST req w default values
  useEffect(() => {
    setIsPreferencesSubmitted(false);
    setShowWarning(false);

    if (
      initialLoadRef.current
      && open
      && userPreferences.hasOpenedModal === false
    ) {
      initialLoadRef.current = false;
      updatePreferences();
    }

    if (
      userPreferences
      && userPreferences.status !== "loading"
      && userPreferences.hasOpenedModal === true
    ) {
      setTradingFocus(userPreferences.preferences.tradingFocus);
      setRiskAppetite(userPreferences.preferences.riskAppetite);
    }
  }, [open, userPreferences]);

  const hasStateChanged
    = userPreferences.preferences
    && (userPreferences.preferences.tradingFocus !== tradingFocus
      || userPreferences.preferences.riskAppetite !== riskAppetite);

  const handleClose = () => {
    if (showWarning || isPreferencesSubmitted) {
      // warning already shown, so can close modal without saving
      setOpen(false);
      return;
    }
    if (hasStateChanged) {
      setShowWarning(true);
    }
    else {
      setOpen(false);
    }
  };

  const handleSubmit = async () => {
    if (!hasStateChanged) {
      setIsPreferencesSubmitted(true);
    }
    else {
      await updatePreferences();
      if (userPreferences.status === "succeeded") {
        setIsPreferencesSubmitted(true);
        dispatch(showNotification({ message: "Preference saved. TradeGPT will now provide advice based on your preferences.", severity: "success", horizontal: "right" }));
      }
    }
  };

  return (
    <>
      {!isPreferencesSubmitted && (
        <Modal open={open} onClose={handleClose}>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 shadow w-modal-sm sm:w-modal-md md:w-modal-lg rounded-lg p-6 bg-white text-mui-black-87 dark:bg-background/paper-elevation-16 dark:text-white">
            <div className="flex flex-col gap-y-6">
              <div className="flex justify-between items-center">
                <Typography variant="h6/medium-500" className="text-mui-black-87 dark:text-white">
                  Preferences
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleClose}
                  className="cross-icon"
                >
                  <CrossIcon className="fill-current" />
                </IconButton>
              </div>
              {showWarning && (
                <WarningModal
                  open={showWarning}
                  handleExit={handleClose}
                  handleCloseWarning={() => setShowWarning(false)}
                  mainText="Your changes will be lost if you close this window now."
                  warningType="preferences"
                />
              )}
              {!showWarning && (
                <>
                  <Typography variant="body1/regular-400" className="text-md">
                    State your preference to help TradeGPT tailor to your
                    current needs.
                  </Typography>
                  <PreferenceItem
                    stateVar={tradingFocus}
                    setter={setTradingFocus}
                    prefType="Trading Focus"
                    options={["equity", "options", "any"]}
                  />
                  <PreferenceItem
                    stateVar={riskAppetite}
                    setter={setRiskAppetite}
                    prefType="Risk Appetite"
                    options={["high", "medium", "small", "any"]}
                    toolTipContent="Your comfort level with potential investment volatility"
                  />
                  <div className="flex flex-col gap-y-4">
                    <Button
                      variant="contained"
                      size="large"
                      color="inherit"
                      className="button-submit button-loading"
                      fullWidth
                      onClick={handleSubmit}
                      disabled={
                        userPreferences.status === "loading"
                        && userPreferences.hasOpenedModal
                      }
                    >
                      {userPreferences.status === "loading"
                      && userPreferences.hasOpenedModal
                        ? (
                            <div className="flex items-center space-x-2">
                              <CircularProgress color="inherit" size={20} />
                              <span>Saving...</span>
                            </div>
                          )
                        : (
                            "Save"
                          )}
                    </Button>

                    <Button
                      onClick={handleClose}
                      variant="outlined"
                      size="large"
                      color="inherit"
                      className="button-outlined"
                      disabled={
                        userPreferences.status === "loading"
                        && userPreferences.hasOpenedModal
                      }
                    >
                      Close & Discard Changes
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
